import { Box } from '@mui/material';
import React, { memo, useEffect, useMemo, useState } from 'react';
import Plot from 'react-plotly.js';
import { ASSET_COLORS, basePlotConfig, useChartTheme } from '../plotTheme';

// Hex colour to rgba, for the band fills
const withAlpha = (hex, alpha) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

// Energy balance per hour: what supplied the site is stacked above zero, where the surplus went below zero.
// Load = Solar + Discharge + Grid import - Charge - Grid export
// Assumes solar is behind the grid meter, same as the live load in the backend
const BANDS = [
  // Above zero, stacked bottom to top
  { key: "solar", name: "Solar", color: ASSET_COLORS.solar, stack: "supply", rank: 4 },
  { key: "discharge", name: "Battery discharge", color: ASSET_COLORS.battery, stack: "supply", rank: 3 },
  { key: "import", name: "Grid import", color: ASSET_COLORS.grid, stack: "supply", rank: 2 },
  // Below zero, stacked top to bottom
  { key: "charge", name: "Battery charge", color: ASSET_COLORS.battery, stack: "surplus", rank: 5 },
  { key: "export", name: "Grid export", color: ASSET_COLORS.grid, stack: "surplus", rank: 6 },
];

const PowerPlot = ({
  tsGrid,
  tsSolar = [],
  tsCharge,
  tsDischg,
  plotHeight,
}) => {

  const [data, setData] = useState([]);
  const { mode, baseLayout, themeTrace } = useChartTheme();

  useEffect(() => {
    // Sum all meters / PCS units into one row per timestamp, so every band shares the same x values
    const rows = {};
    const present = new Set();
    const addSeries = (key, series, yField) => {
      series.forEach(dataset => {
        dataset.x.forEach((x, i) => {
          const y = dataset[yField][i];
          if (x == null || y == null) return;
          rows[x] = rows[x] ?? {};
          rows[x][key] = (rows[x][key] ?? 0) + y;
          present.add(key);
        });
      });
    };
    addSeries("import", tsGrid, "yImport");
    addSeries("export", tsGrid, "yExport");
    addSeries("solar", tsSolar, "y");
    addSeries("discharge", tsDischg, "y");
    addSeries("charge", tsCharge, "y");

    // Timestamps are "YYYY-MM-DD HH:mm:ss", so string order is time order
    const x = Object.keys(rows).sort();
    const values = (key) => x.map(t => rows[t][key] ?? 0);

    let _data = [];

    // Bands, skipping assets the site doesn't have (or that never reported)
    BANDS.filter(band => present.has(band.key)).forEach(band => {
      const v = values(band.key);
      const isSurplus = band.stack === "surplus";
      _data.push({
        x: x,
        y: isSurplus ? v.map(y => -y) : v,
        customdata: v, // Positive value for the tooltip
        type: "scatter",
        mode: "lines",
        name: band.name,
        line: { color: band.color, width: 1 },
        fillcolor: withAlpha(band.color, isSurplus ? 0.25 : 0.6),
        stackgroup: band.stack,
        legendrank: band.rank,
        hovertemplate: `${band.name}: %{customdata:,.1f} kW<extra></extra>`,
      });
    });

    // Load on top, as its own line so it can be hovered
    if (x.length > 0) {
      const load = x.map(t => {
        const r = rows[t];
        return (r.solar ?? 0) + (r.discharge ?? 0) + (r.import ?? 0) - (r.charge ?? 0) - (r.export ?? 0);
      });
      _data.push({
        x: x,
        y: load,
        type: "scatter",
        mode: "lines",
        name: "Load",
        line: { color: ASSET_COLORS.load, width: 2.5 },
        legendrank: 1,
        hovertemplate: "Load: %{y:,.1f} kW<extra></extra>",
      });
    }

    setData(_data);
  }, [tsGrid, tsSolar, tsCharge, tsDischg]);

  // Stable props, so Plotly only redraws when the data or theme changes (redraws block the energy-flow animation)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const themedData = useMemo(() => data.map(themeTrace), [data, mode]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const layout = useMemo(() => baseLayout("Power", " kW"), [mode]);

  return (

    <Box sx={{
      width: "100%",
      height: plotHeight,
    }}>
      <Plot
        data={themedData}
        layout={layout}
        config={basePlotConfig}
        useResizeHandler={true}
        style={{ width: "100%", height: "100%" }}
      />

    </Box>
  )
}

export default memo(PowerPlot);
